import { Component, OnDestroy, OnInit } from '@angular/core';
import { AdvancedProductSearchDto, SearchDtoSearchTypeEnum } from '@scip/api';
import { OrganizationDto } from '@scip/api/model/organization-dto';
import { OrganizationGroupDto } from '@scip/api/model/organization-group-dto';
import { combineLatest, of, ReplaySubject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import { DateUtils, Feature, SearchUtils, translationKeys } from '../../../../core';
import {
  FeatureService,
  OrganizationGroupService,
  ProductSearch,
  ProfileService,
  RecentSearch,
  RecentSearchTypeEnum,
  SearchService
} from '../../../../services';
import { EditableItemConfiguration, MultiInput, MultiInputConfiguration } from '../../../../shared';

@Component({
  selector: 'scip-products-table-search',
  templateUrl: './products-table-search.component.html',
  styleUrls: ['./products-table-search.component.scss']
})
export class ProductsTableSearchComponent implements OnInit, OnDestroy {
  // Quick search
  multiInputConfigurationQuickSearch: MultiInput;
  quickSearchInput: EditableItemConfiguration = SearchUtils.createLabelledInputSearchConfig(translationKeys.search.service.productSearch);

  // Advanced search
  multiInputConfigurationAdvSearch: MultiInput;
  uploadTypeConfig: EditableItemConfiguration = SearchUtils.createUploadTypeSearchConfig();
  organizationConfig: EditableItemConfiguration = SearchUtils.createOrganizationMultiSelectSearchConfig();
  productNameConfig: EditableItemConfiguration = SearchUtils.createLabelledInputSearchConfig(translationKeys.search.service.productName);
  productNumberConfig: EditableItemConfiguration = SearchUtils.createLabelledInputSearchConfig(translationKeys.search.service.productNumber);
  productLoadingState:  EditableItemConfiguration = SearchUtils.createIncludeLoadingStateSearchConfig();
  lastUpdatedConfig: EditableItemConfiguration = SearchUtils.createFromToDatePickerConfig(translationKeys.productsComponent.typescript.lastUpdated);
  lastSubmissionDateConfig: EditableItemConfiguration = SearchUtils.createFromToDatePickerConfig(
    translationKeys.productsComponent.typescript.lastSubmissionDate
  );
  scipRelevantChangedConfig: EditableItemConfiguration = SearchUtils.createFromToDatePickerConfig(
    translationKeys.productsTableComponent.typescript.scipRelevantChanged
  );
  euMarketRelevantChangedConfig: EditableItemConfiguration = SearchUtils.createFromToDatePickerConfig(
    translationKeys.productsTableComponent.typescript.euMarketRelevantChanged
  );
  orgGroupConfig: EditableItemConfiguration;

  organizationSuggestions: OrganizationDto[] = [];
  orgGroupSuggestions: OrganizationGroupDto[] = [];

  recentSearchEnum: typeof RecentSearchTypeEnum = RecentSearchTypeEnum;
  translationKeys: typeof translationKeys = translationKeys;

  private findButtonLabel = translationKeys.multiInputComponent.html.findButtonLabel;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private searchService: SearchService,
    private featureService: FeatureService,
    private organizationGroupService: OrganizationGroupService,
    private profileService: ProfileService
  ) {}

  ngOnInit(): void {
    const productSearch = this.searchService.getProductSearchEvents().pipe(
      map((whole: ProductSearch) => whole.recentSearch),
      filter((event: RecentSearch) => !!event),
      takeUntil(this.destroyed$)
    );

    const organizations = this.profileService.getAllOrganizations(true, false, true).pipe(tap((allOrgs) => (this.organizationSuggestions = allOrgs)));

    let organizationGroups = of([]);
    if (this.featureService.isActive(Feature.LE_GROUPING)) {
      organizationGroups = this.organizationGroupService.getAllOrganizationGroups(true).pipe(tap((allGroups) => (this.orgGroupSuggestions = allGroups)));
    }
    const orgsAndOrgGroups = combineLatest([organizations, organizationGroups]).pipe(tap(() => this.createSearchConfiguration()));

    combineLatest([productSearch, orgsAndOrgGroups])
      .pipe(
        takeUntil(this.destroyed$),
        map(([recentSearch]) => recentSearch)
      )
      .subscribe((recentSearch) => {
        this.setRecentSearchParams(recentSearch);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private createSearchConfiguration(): void {
    this.createQuickSearchConfiguration();
    this.createAdvancedSearchConfiguration();
  }

  private createQuickSearchConfiguration(): void {
    const quickMultiInputConfiguration: MultiInputConfiguration = {
      configurableItems: [this.quickSearchInput]
    };
    this.multiInputConfigurationQuickSearch = {
      showHref: false,
      showClearButton: true,
      actionButtonIcon: 'icon-content-search',
      actionButtonLabel: this.findButtonLabel,
      actionButtonAction: (isEmptySearch: boolean) => this.quickSearchAction(isEmptySearch),
      multiInputConfigurations: [quickMultiInputConfiguration]
    };
  }

  private quickSearchAction(isEmptySearch: boolean): void {
    if (!isEmptySearch) {
      const searchObject: Record<string, string> = {
        searchTerm: this.quickSearchInput.content
      };
      this.searchService.createSearch(SearchDtoSearchTypeEnum.ProductQuick, searchObject);
    } else {
      // User cleared quicksearch-input and clicked "Find"-button
      this.searchService.emitProductSearchEvent({ searchId: undefined, recentSearch: undefined });
    }
  }

  private createAdvancedSearchConfiguration(): void {
    this.organizationConfig.suggestions = this.organizationSuggestions;

    // create multiInputConfiguration for Submission Information containing uploadType and Organization
    const editableItems: EditableItemConfiguration[] = [];
    editableItems.push(this.uploadTypeConfig);
    editableItems.push(this.organizationConfig);
    //Enable search for legal entity groups only if the legal entity group feature is enabled
    if (this.featureService.isActive(Feature.LE_GROUPING)) {
      this.orgGroupConfig = SearchUtils.createLegalEntityGroupSearchConfig();
      this.orgGroupConfig.suggestions = this.orgGroupSuggestions;
      editableItems.push(this.orgGroupConfig);
    }
    editableItems.push(this.lastUpdatedConfig);
    editableItems.push(this.lastSubmissionDateConfig);
    editableItems.push(this.scipRelevantChangedConfig);
    editableItems.push(this.euMarketRelevantChangedConfig);

    const submissionSearch: MultiInputConfiguration = {
      configurableItems: editableItems,
      header: translationKeys.search.service.submissionInformation
    };

    // create multiInputConfiguration for Product Information
    const productInformationInputs: EditableItemConfiguration[] = [];
    productInformationInputs.push(this.productNameConfig);
    productInformationInputs.push(this.productNumberConfig);
    productInformationInputs.push(this.productLoadingState);
    const productSearch: MultiInputConfiguration = {
      configurableItems: productInformationInputs,
      header: translationKeys.search.service.productInformation
    };

    const multiInputConfigs: MultiInputConfiguration[] = [];
    multiInputConfigs.push(productSearch);
    multiInputConfigs.push(submissionSearch);

    // Define Multi Input
    this.multiInputConfigurationAdvSearch = {
      showHref: false,
      showClearButton: true,
      actionButtonIcon: 'icon-content-search',
      actionButtonLabel: this.findButtonLabel,
      actionButtonAction: (isEmptySearch: boolean) => this.advancedSearchAction(isEmptySearch),
      multiInputConfigurations: multiInputConfigs
    };
  }

  private advancedSearchAction(isEmptySearch: boolean): void {
    if (isEmptySearch) {
      // User cleared advanced-search-input and clicked "Find"-button, we still want to refresh the table, but we do not want to create a real search for it
      this.searchService.emitProductSearchEvent({ searchId: undefined, recentSearch: undefined });
    } else {
      const lastUpdatedFromDate: Date = this.lastUpdatedConfig.selection[0];
      const lastUpdatedToDate: Date = this.lastUpdatedConfig.selection[1];
      const lastSubmissionFromDate: Date = this.lastSubmissionDateConfig.selection[0];
      const lastSubmissionToDate: Date = this.lastSubmissionDateConfig.selection[1];
      const scipRelevantChangedFromDate: Date = this.scipRelevantChangedConfig.selection[0];
      const scipRelevantChangedToDate: Date = this.scipRelevantChangedConfig.selection[1];
      const euMarketRelevantChangedFromDate: Date = this.euMarketRelevantChangedConfig.selection[0];
      const euMarketRelevantChangedToDate: Date = this.euMarketRelevantChangedConfig.selection[1];

      const searchParams: AdvancedProductSearchDto = {
        productName: this.productNameConfig.content,
        productNumber: this.productNumberConfig.content,
        productLoadingState: this.productLoadingState.inputs[0].selectedValue,
        requestedType: this.uploadTypeConfig.content.value,
        organizationsIds: this.organizationConfig.selection.map((selectedOrg: OrganizationDto) => selectedOrg.identifier),
        lastUpdatedFrom: lastUpdatedFromDate ? DateUtils.convertDateForBackend(lastUpdatedFromDate) : '',
        lastUpdatedTo: lastUpdatedToDate ? DateUtils.convertDateForBackend(lastUpdatedToDate) : '',
        submissionDateFrom: lastSubmissionFromDate ? DateUtils.convertDateForBackend(lastSubmissionFromDate) : '',
        submissionDateTo: lastSubmissionToDate ? DateUtils.convertDateForBackend(lastSubmissionToDate) : '',
        scipRelevantChangedFrom: scipRelevantChangedFromDate ? DateUtils.convertDateForBackend(scipRelevantChangedFromDate) : '',
        scipRelevantChangedTo: scipRelevantChangedToDate ? DateUtils.convertDateForBackend(scipRelevantChangedToDate) : '',
        euMarketRelevantChangedFrom: euMarketRelevantChangedFromDate ? DateUtils.convertDateForBackend(euMarketRelevantChangedFromDate) : '',
        euMarketRelevantChangedTo: euMarketRelevantChangedToDate ? DateUtils.convertDateForBackend(euMarketRelevantChangedToDate) : '',
        organizationGroupIds: []
      };

      // Only if organization groups feature is enabled, we consider the selecion of org group multi input
      if (this.featureService.isActive(Feature.LE_GROUPING)) {
        searchParams.organizationGroupIds = this.orgGroupConfig.selection.map((selectedOrgGroup: OrganizationGroupDto) => selectedOrgGroup.identifier);
      }
      this.searchService.createSearch(SearchDtoSearchTypeEnum.ProductAdvanced, searchParams);
    }
  }

  private setRecentSearchParams(entry: RecentSearch): void {
    if (entry.productLastSearchesDto.searchObject) {
      this.quickSearchInput.content = entry.productLastSearchesDto.searchObject;
    } else if (entry.productLastSearchesDto.searchDto) {
      this.setOrganizations(entry.productLastSearchesDto.searchDto.organizationsIds);
      // Only consider org groups if the org group feature is enabled
      if (this.featureService.isActive(Feature.LE_GROUPING)) {
        this.setOrganizationGroups(entry.productLastSearchesDto.searchDto.organizationGroupIds);
      }
      this.productNameConfig.content = entry.productLastSearchesDto.searchDto.productName;
      this.productNumberConfig.content = entry.productLastSearchesDto.searchDto.productNumber;
      this.productLoadingState.inputs[0].selectedValue = entry.productLastSearchesDto.searchDto.productLoadingState;
      this.setUploadType(entry.productLastSearchesDto.searchDto.requestedType);
      this.setLastUpdated(entry.productLastSearchesDto.searchDto);
      this.setLastSubmissionDate(entry.productLastSearchesDto.searchDto);
      this.setScipRelevantChanged(entry.productLastSearchesDto.searchDto);
      this.setEuMarketRelevantChanged(entry.productLastSearchesDto.searchDto);
    }
  }

  private setLastUpdated(search: AdvancedProductSearchDto): void {
    this.lastUpdatedConfig.selection = [];
    if (search.lastUpdatedFrom) {
      this.lastUpdatedConfig.selection.push(new Date(search.lastUpdatedFrom));
    } else {
      this.lastUpdatedConfig.selection.push(undefined);
    }
    if (search.lastUpdatedTo) {
      this.lastUpdatedConfig.selection.push(new Date(search.lastUpdatedTo));
    } else {
      this.lastUpdatedConfig.selection.push(undefined);
    }
  }

  private setLastSubmissionDate(search: AdvancedProductSearchDto): void {
    this.lastSubmissionDateConfig.selection = [];
    if (search.submissionDateFrom) {
      this.lastSubmissionDateConfig.selection.push(new Date(search.submissionDateFrom));
    } else {
      this.lastSubmissionDateConfig.selection.push(undefined);
    }
    if (search.submissionDateTo) {
      this.lastSubmissionDateConfig.selection.push(new Date(search.submissionDateTo));
    } else {
      this.lastSubmissionDateConfig.selection.push(undefined);
    }
  }

  private setScipRelevantChanged(search: AdvancedProductSearchDto): void {
    this.scipRelevantChangedConfig.selection = [];
    if (search.scipRelevantChangedFrom) {
      this.scipRelevantChangedConfig.selection.push(new Date(search.scipRelevantChangedFrom));
    } else {
      this.scipRelevantChangedConfig.selection.push(undefined);
    }
    if (search.scipRelevantChangedTo) {
      this.scipRelevantChangedConfig.selection.push(new Date(search.scipRelevantChangedTo));
    } else {
      this.scipRelevantChangedConfig.selection.push(undefined);
    }
  }

  private setEuMarketRelevantChanged(search: AdvancedProductSearchDto): void {
    this.euMarketRelevantChangedConfig.selection = [];
    if (search.euMarketRelevantChangedFrom) {
      this.euMarketRelevantChangedConfig.selection.push(new Date(search.euMarketRelevantChangedFrom));
    } else {
      this.euMarketRelevantChangedConfig.selection.push(undefined);
    }
    if (search.euMarketRelevantChangedTo) {
      this.euMarketRelevantChangedConfig.selection.push(new Date(search.euMarketRelevantChangedTo));
    } else {
      this.euMarketRelevantChangedConfig.selection.push(undefined);
    }
  }

  private setUploadType(reqType: any): void {
    this.uploadTypeConfig.content = this.uploadTypeConfig.inputs.find((input) => input.value === reqType);
  }

  private setOrganizations(ids: string[]): void {
    ids.forEach((id) => {
      const foundOrganization = this.organizationConfig.suggestions.find((suggestion: OrganizationDto) => suggestion.identifier === id);
      if (foundOrganization) {
        this.organizationConfig.selection.push(foundOrganization);
      }
    });
  }

  private setOrganizationGroups(ids: string[]): void {
    ids.forEach((id) => {
      const foundOrgGroup = this.orgGroupConfig.suggestions.find((suggestion: OrganizationGroupDto) => suggestion.identifier === id);
      if (foundOrgGroup) {
        this.orgGroupConfig.selection.push(foundOrgGroup);
      }
    });
  }
}
